//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Types
import type { Visit, Test, FlaggingRequest, FlaggingResult } from "../../services/api/domains/lis/lis.types";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * LIS (Laboratory Information System) Integration Composable
 *
 * Manages communication with external LIS system for patient visit data,
 * test history, and result flagging calculations.
 *
 * Responsibilities:
 * - Fetch patient visit information by sample barcode
 * - Retrieve historical test results for trend analysis
 * - Calculate result flagging (normal/abnormal indicators)
 * - Cache visit and flagging data to minimize API calls
 * - Handle cache key updates when barcodes change
 * - Persist visit cache to session storage for offline access
 * - Handle "no visit" scenarios gracefully (cache null values)
 *
 * @param key - Unique key for state isolation (default: "lis")
 */
export const useLis = (key = "lis") => {
  //
  // ============================================================================
  // COMPOSABLES
  // ============================================================================
  //

  const { withAbort, abortAll } = useAbortableMultiFetch();
  const { handleError } = useErrorBoundary();

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //
  const error = useState<string | null>(`${key}-error`, () => null);

  const loading = useState<boolean>(`${key}-loading`, () => false);
  const historyLoading = useState<boolean>(`${key}-history-loading`, () => false);

  // Cache for visit data (barcode -> visit OR null if no visit exists)
  const visitCache = useState<Record<string, Visit | null>>(`${key}-visit-cache`, () => {
    if (import.meta.client) {
      const cached = sessionStorage.getItem(`lis-visits-${key}`);
      if (cached) {
        try {
          return JSON.parse(cached);
        } catch (e) {
          console.error("Failed to parse cached visits:", e);
        }
      }
    }
    return {};
  });
  const flaggingCache = useState<Record<string, FlaggingResult[]>>(`${key}-flagging-cache`, () => ({}));

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  /**
   * Check if visit data is cached for a specific sample
   *
   * Purpose:
   * Determine whether to fetch visit from API or use cached data.
   * Checks for key existence (including null values) to prevent redundant 404 requests.
   *
   * Responsibilities:
   * - Convert barcode to uppercase for case-insensitive lookup
   * - Return true if key exists in cache (even if value is null)
   * - This includes "no visit found" (null) scenarios
   *
   * Why Cache Null Values:
   * - LIS may not have visit for every sample
   * - 404 responses should be cached to avoid repeated failed requests
   * - Improves performance for samples without LIS integration
   *
   * @param barcode - Sample barcode to check (case-insensitive)
   * @returns True if cached (including null/no visit), false if needs fetch
   */
  const hasCachedVisitFor = (barcode: string) => {
    const barcodeKey = barcode.toUpperCase();

    // Check if key exists in cache (even if value is null)
    return barcodeKey in visitCache.value;
  };

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Fetch patient visit information by sample barcode
   *
   * Purpose:
   * Retrieve LIS visit data including patient demographics, examinations,
   * and ordered tests for validation workflow.
   *
   * Responsibilities:
   * - Check cache before fetching (unless force refresh)
   * - Show loading only when fetching without cache
   * - Handle 404 responses (no visit found) gracefully
   * - Cache both successful visits AND null (no visit)
   * - Persist cache to session storage for offline access
   * - Convert barcode to uppercase for cache key consistency
   *
   * Business Logic - 404 Handling:
   * Not all samples have LIS visits (direct entry, offline samples, etc.)
   * - Cache null value when 404 or "not found" error occurs
   * - Prevents repeated API calls for samples without visits
   * - UI can check for null to show "no visit" state
   *
   * @param sampleBarcode - Sample barcode (case-insensitive)
   * @param forceRefresh - If true, bypass cache and fetch fresh data
   * @returns Visit object, null if no visit exists, or null if request aborted
   */
  const fetchVisitBySample = async (sampleBarcode: string, forceRefresh = false): Promise<Visit | null> => {
    // Only show loading if no cached data OR force refresh
    if (!hasCachedVisitFor(sampleBarcode) || forceRefresh) {
      loading.value = true;
    }
    error.value = null;

    try {
      const response = await withAbort<Visit>(async (signal) => {
        const cl = await useProdClient();
        return cl.lis.fetchVisitBySample(sampleBarcode, signal);
      });

      // Only cache a real Visit response. `withAbort` returns null when the
      // request is aborted — we must NOT write that null into the cache, because
      // it would corrupt a previously-valid (or not-yet-fetched) entry.
      // The authoritative "no visit" null (404) is already handled in the catch
      // block below and written there instead.
      if (response !== null) {
        visitCache.value[sampleBarcode.toUpperCase()] = response;

        if (import.meta.client) {
          sessionStorage.setItem(`lis-visits-${key}`, JSON.stringify(visitCache.value));
        }
      }

      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchVisitBySample",
        fallbackKey: "components.common.errors.fetchFailed",
      });

      // Handle abort silently
      if (result.type === "abort") return null;

      // If error is 404 (no visit exists), cache null to prevent redundant requests
      if (result.statusCode === 404) {
        visitCache.value[sampleBarcode.toUpperCase()] = null;

        if (import.meta.client) {
          sessionStorage.setItem(`lis-visits-${key}`, JSON.stringify(visitCache.value));
        }

        return null;
      }

      error.value = result.message || `Failed to fetch visit for ${sampleBarcode}`;
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Clear all cached visit data
   *
   * Purpose:
   * Force reset of visit cache to fetch fresh LIS data.
   * Used when user explicitly requests refresh or after significant LIS changes.
   *
   * Responsibilities:
   * - Remove visit cache from session storage
   * - Clear in-memory visit cache object
   *
   * Note: Does NOT clear flagging cache (separate lifecycle)
   * Note: Does NOT abort ongoing requests - call abortAll() separately
   */
  const clearCache = (): void => {
    if (import.meta.client) {
      sessionStorage.removeItem(`lis-visits-${key}`);
    }
    visitCache.value = {};
  };

  /**
   * Clear all cached flagging data
   *
   * Purpose:
   * Force re-calculation of clinical flags on next sample visit.
   * Called when the user explicitly refreshes (full cache clear) so that
   * stale flag data from the previous session is not shown.
   *
   * Note: flaggingCache is in-memory only (not sessionStorage), so it is
   * NOT cleared by clearCache() which only clears the visit sessionStorage.
   */
  const clearFlaggingCache = (): void => {
    flaggingCache.value = {};
  };

  /**
   * Fetch historical test results for patient
   *
   * Purpose:
   * Retrieve past results for the same test to enable trend analysis
   * and comparison in patient history dialog.
   *
   * Responsibilities:
   * - Fetch historical results from LIS for specific test
   * - Use dedicated loading state (historyLoading) to not conflict with main loading
   * - Handle request abortion
   * - Return historical data or null
   *
   * Note: Results are NOT cached (history dialog is transient)
   *
   * @param sampleBarcode - Current sample barcode for patient lookup
   * @param testId - Test identifier to retrieve history for
   * @returns Array of historical test results or null if aborted/error
   */
  const fetchPatientHistory = async (sampleBarcode: string, testId: string): Promise<Test[] | null> => {
    historyLoading.value = true;
    error.value = null;

    try {
      const response = await withAbort<Test[]>(async (signal) => {
        const cl = await useProdClient();
        return cl.lis.fetchPatientHistory(sampleBarcode, testId, signal);
      });

      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "fetchPatientHistory",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || `Failed to fetch patient history for test ${testId}`;
      return null;
    } finally {
      historyLoading.value = false;
    }
  };

  /**
   * Calculate result flagging indicators (normal/abnormal/critical)
   *
   * Purpose:
   * Determine if test results are within normal ranges based on patient demographics
   * and reference intervals from LIS.
   *
   * Responsibilities:
   * - Generate stable cache key from barcode + sorted test IDs
   * - Return cached flagging if available (expensive calculation)
   * - Send test results to LIS for flagging calculation
   * - Cache calculated flagging for reuse
   * - Handle request abortion
   *
   * Why Cache:
   * - Flagging calculation is expensive (involves reference intervals)
   * - Results don't change without data modification
   * - Enables instant UI updates on result review
   *
   * @param data - Array of test results to evaluate for flagging
   * @param sampleBarcode - Optional barcode for stable cache key (recommended)
   * @returns Array of flagging results or null if aborted/error
   */
  const calculateFlagging = async (
    data: FlaggingRequest,
    sampleBarcode?: string,
    forceRefresh = false,
  ): Promise<FlaggingResult[] | null> => {
    // Generate stable cache key from barcode + test IDs
    const testIds = data
      .map((item) => item.test.id)
      .sort()
      .join(",");

    const cacheKey = sampleBarcode ? `${sampleBarcode.toUpperCase()}_${testIds}` : JSON.stringify(data);

    // Return cached if available and not forcing a refresh
    if (!forceRefresh && flaggingCache.value[cacheKey]) {
      return flaggingCache.value[cacheKey];
    }

    loading.value = true;
    error.value = null;

    try {
      // withAbort wraps useProdClient() so the AbortController is registered
      // synchronously before any await — abortAll() can always cancel this.
      const response = await withAbort<FlaggingResult[]>(async (signal) => {
        const cl = await useProdClient();
        return cl.lis.calculateFlagging(data, signal);
      });

      // Cache the result
      if (response) {
        flaggingCache.value[cacheKey] = response;
      }

      return response;
    } catch (err: any) {
      const result = handleError(err, {
        operation: "calculateFlagging",
        fallbackKey: "components.common.errors.fetchFailed",
      });
      if (result.type === "abort") return null;
      error.value = result.message || "Failed to calculate flagging";
      return null;
    } finally {
      loading.value = false;
    }
  };

  /**
   * Update cache keys when sample barcode changes
   *
   * Purpose:
   * Synchronize visit and flagging cache keys after barcode update.
   * Must be called after updating sample barcode to maintain cache consistency.
   *
   * Responsibilities:
   * - Move visit cache entry from old key to new key
   * - Update all flagging cache keys that include old barcode
   * - Preserve cache data (don't invalidate)
   * - Update session storage with new keys
   * - Handle both visit and flagging caches atomically
   *
   * Note: This is a cache-only operation, no API calls
   *
   * @param oldBarcode - Original sample barcode
   * @param newBarcode - New sample barcode
   */
  const updateVisitCacheKey = (oldBarcode: string, newBarcode: string): void => {
    const oldKey = oldBarcode.toUpperCase();
    const newKey = newBarcode.toUpperCase();

    // Update visit cache
    if (visitCache.value[oldKey] !== undefined) {
      visitCache.value[newKey] = visitCache.value[oldKey];
      delete visitCache.value[oldKey];
    }

    // Update flagging cache keys
    const flaggingKeysToUpdate: string[] = [];
    Object.keys(flaggingCache.value).forEach((key) => {
      if (key.startsWith(`${oldKey}_`)) {
        flaggingKeysToUpdate.push(key);
      }
    });

    flaggingKeysToUpdate.forEach((oldFlagKey) => {
      const testIds = oldFlagKey.substring(oldKey.length); // "_testId1,testId2"
      const newFlagKey = `${newKey}${testIds}`;

      const cachedValue = flaggingCache.value[oldFlagKey];
      if (cachedValue !== undefined) {
        flaggingCache.value[newFlagKey] = cachedValue;
      }
      delete flaggingCache.value[oldFlagKey];
    });

    // Update sessionStorage
    if (import.meta.client) {
      sessionStorage.setItem(`lis-visits-${key}`, JSON.stringify(visitCache.value));
    }
  };

  /**
   * Look up cached flagging for a barcode + result set synchronously.
   *
   * Builds the same stable cache key used by calculateFlagging so the
   * data-fetching layer can paint flags alongside cached results/visit
   * with zero latency — no network call, no await.
   *
   * @param barcode - Sample barcode (case-insensitive)
   * @param results - Results array used to derive the test-ID portion of the key
   * @returns Cached flagging array, or null if not yet calculated
   */
  const getCachedFlaggingFor = (barcode: string, results: { mappingKey: string | null }[]): FlaggingResult[] | null => {
    const testIds = results
      .map((r) => r.mappingKey)
      .sort()
      .join(",");
    const cacheKey = `${barcode.toUpperCase()}_${testIds}`;
    return flaggingCache.value[cacheKey] ?? null;
  };

  return {
    // State
    error,

    loading: readonly(loading),
    historyLoading: readonly(historyLoading),

    // Computed
    visitCache,
    hasCachedVisitFor,
    getCachedFlaggingFor,

    // Methods
    fetchVisitBySample,
    fetchPatientHistory,
    calculateFlagging,
    updateVisitCacheKey,

    clearCache,
    clearFlaggingCache,
    abortAll,
  };
};
